import logomarkForestAsset from '@/assets/images/logos/logomark-forest.svg';
import { ConnectionStatus, DataFlow } from '../../catalog/enums';
import { getIntegrationName } from '../../catalog/ids';
import { ProductApp, type IntegrationConnection } from '../view-model/types';

/** The connections table columns, keyed by the connection field each sorts/reads. */
export const ConnectionColumn = {
  type: 'dataFlow',
  name: 'name',
  status: 'status',
  connections: 'connectedTo',
  dateAdded: 'createdAt',
  lastActivity: 'lastActivity',
} as const;
export type ConnectionColumn = (typeof ConnectionColumn)[keyof typeof ConnectionColumn];

export type ConnectionSorting = { id: string; desc: boolean };
export const DEFAULT_SORTING: ConnectionSorting = { id: ConnectionColumn.status, desc: false };

/** The auto-track pipelines-sources group key (`type-product-dataFlow-isAutoTrack`). */
export const TRACK_SOURCES_GROUP_KEY = `customerio-${ProductApp.dataPipelines}-${DataFlow.in}-true`;

export const FILTER_BREAKPOINT = 5;

const STATUS_SORT_ORDER: readonly (ConnectionStatus | undefined)[] = [
  ConnectionStatus.error,
  ConnectionStatus.authenticationError,
  ConnectionStatus.configurationError,
  ConnectionStatus.connected,
  ConnectionStatus.noRecentData,
  ConnectionStatus.needsAttention,
  ConnectionStatus.multiple,
  ConnectionStatus.disabled,
  ConnectionStatus.disconnected,
];

const DATA_FLOW_SORT_ORDER: readonly (DataFlow | null | undefined)[] = [DataFlow.in, DataFlow.out];

function safeIndexOf<T>(order: readonly T[], value?: T): number {
  const index = value != null ? order.indexOf(value) : -1;
  return index > -1 ? index : order.length + 1;
}

function connectionCount(connection: IntegrationConnection): number {
  return connection.product === ProductApp.journeys ? 1 : (connection.connectedTo?.length ?? 0);
}

/** The per-column comparators (ascending). Ported from the Ember table's `SORTING`. */
export const SORTING: Record<string, (a: IntegrationConnection, b: IntegrationConnection) => number> = {
  [ConnectionColumn.type]: (a, b) =>
    safeIndexOf(DATA_FLOW_SORT_ORDER, a.dataFlow) - safeIndexOf(DATA_FLOW_SORT_ORDER, b.dataFlow),
  [ConnectionColumn.name]: (a, b) => a.name.localeCompare(b.name),
  [ConnectionColumn.status]: (a, b) =>
    safeIndexOf(STATUS_SORT_ORDER, a.status) - safeIndexOf(STATUS_SORT_ORDER, b.status),
  [ConnectionColumn.connections]: (a, b) => connectionCount(a) - connectionCount(b),
  [ConnectionColumn.dateAdded]: (a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0),
  [ConnectionColumn.lastActivity]: (a, b) => (a.lastActivity ?? 0) - (b.lastActivity ?? 0),
};

/** The display name for a connection's "type" (integration) column/subtext. */
export function getTypeName(connection: IntegrationConnection | undefined): string {
  if (connection == null) {
    return '';
  }
  if (connection.product === ProductApp.journeys) {
    const name = getIntegrationName(connection.type);
    if (name != null) {
      return name;
    }
    return connection.type ?? connection.name;
  }
  if (connection.type === 'customerio') {
    return connection.isAutoTrack === true ? 'Customer.io API' : 'Customer.io';
  }
  return connection.integrationName ?? getIntegrationName(connection.type) ?? connection.type ?? '';
}

export interface ConnectionFilters {
  searchText: string;
  statusFilter: ConnectionStatus | 'All';
  dataFlowFilter: DataFlow | 'All';
}

/**
 * Filter by name search + status + data-flow, then sort by the active column.
 *
 * This is the pre-grouping pass. It does not decide the rendered row order: the
 * DataTable sorts the grouped rows again with TanStack's own comparators (see the
 * `isManual: false` note in `connections-table.tsx`), same as Ember. What this pass
 * still decides is the order *within* a group (the expanded child rows) and the
 * tie-break order between rows TanStack considers equal, since both sorts are stable.
 */
export function filterAndSortConnections(
  connections: readonly IntegrationConnection[],
  filters: ConnectionFilters,
  sorting: ConnectionSorting,
): IntegrationConnection[] {
  const filtered = connections.filter(
    (connection) =>
      (filters.searchText === '' || connection.name.toLowerCase().includes(filters.searchText.toLowerCase())) &&
      (filters.statusFilter === 'All' || connection.status === filters.statusFilter) &&
      (filters.dataFlowFilter === 'All' || connection.dataFlow === filters.dataFlowFilter),
  );
  const sortFn = SORTING[sorting.id];
  if (sortFn == null) {
    return filtered;
  }
  return [...filtered].sort((a, b) => sortFn(a, b) * (sorting.desc ? -1 : 1));
}

/**
 * Group connections that share a `type-product-dataFlow-isAutoTrack` key.
 * Auto-track sources always nest (a parent + its children); other types nest
 * only when there is more than one. Ported from the Ember table's
 * `groupedConnections`.
 */
export function groupConnections(
  connections: readonly IntegrationConnection[],
  shouldGroupAutoTrack: boolean,
): IntegrationConnection[] {
  const groups = new Map<string, IntegrationConnection[]>();
  for (const connection of connections) {
    const isAutoTrack = connection.product === ProductApp.dataPipelines ? connection.isAutoTrack === true : false;
    const key = `${connection.type}-${connection.product}-${connection.dataFlow}-${isAutoTrack}`;
    const bucket = groups.get(key);
    if (bucket == null) {
      groups.set(key, [connection]);
    } else {
      bucket.push(connection);
    }
  }

  return [...groups.entries()].map(([key, grouped]) => {
    const [first] = grouped;
    // Don't nest a single connection unless it is the (always-nested) auto-track group.
    if ((!shouldGroupAutoTrack || key !== TRACK_SOURCES_GROUP_KEY) && grouped.length === 1) {
      return first;
    }

    const isAutoTrack = key === TRACK_SOURCES_GROUP_KEY;
    let icon = first.icon;
    let imageSrc = first.imageSrc;
    let name = getTypeName(first);
    if (isAutoTrack) {
      icon = undefined;
      imageSrc = logomarkForestAsset;
      name = 'Customer.io API: Track';
    }

    return {
      id: `${key}-group`,
      type: first.type,
      path: first.path,
      dataFlow: first.dataFlow,
      icon,
      imageSrc,
      name,
      product: first.product,
      isAutoTrack,
      status: grouped.some((c) => c.status !== first.status) ? ConnectionStatus.multiple : first.status,
      connectedTo: undefined,
      createdAt: undefined,
      lastActivity: undefined,
      href: undefined,
      children: grouped,
    } satisfies IntegrationConnection;
  });
}
